import { computed, onMounted, reactive, ref, watch } from "vue";
import xlsx from "xlsx";
import feather from "feather-icons";
import { TabulatorFull as Tabulator } from "tabulator-tables";
import { useStore } from "@/store";
import Swal from "sweetalert2";
import { customSwalMessageBuilder } from "./utils/customSwalMessageBuilder";
import { useRoute } from "vue-router";
import { refundRequestEnums } from "./enums/refundRequestEnums";
import { authGetterService } from "../services/auth/auth-getter-service";
import fileDownload from "js-file-download";
import { handleErrorResponse } from "@/services/auth/responseHandler/handle-response-service";

export const allRefundRequests = () => {
  const {
    buildSwalWarning,
    buildSwalSuccess,
    buildSwalHtmlError
  } = customSwalMessageBuilder();
  const { getStatusParamForRefundRequestViews } = refundRequestEnums();
  const tableRef = ref();
  const tabulator = ref();
  const filter = reactive({
    field: "fullname",
    type: "like",
    value: "",
    from: "",
    to: ""
  });
  const fromDate = reactive({
    field: "from",
    type: ">=",
    value: ""
  });
  const toDate = reactive({
    field: "to",
    type: "<=",
    value: ""
  });
  const newStatus = ["New"];

  const errorStatus = ["Pre Refund Check Failed", "Refunding Failed"];

  const approvedStatus = ["Approved"];

  const successStatus = ["Refunded"];

  const cancelledStatus = ["Rejected"];

  const approvalRejectionDataObject = reactive({
    uuid: "",
    note: "",
    title: "",
    type: ""
  });

  const route = useRoute();
  const path = computed(() => route.path);
  const statusParam = getStatusParamForRefundRequestViews(path.value);
  const displayedDate =
    path.value === "/completed-refunds" ? "REFUNDED ON" : "REQUESTED ON";
  const baseUrlSuffix = statusParam !== "" ? "?" + statusParam : "";
  const store = useStore();
  const adminUser = computed(() => store.state.auth.loggedUser);
  const totalRefundedFund = ref(0);
  const totalRefunds = ref(0);

  const refundPreCheckFailureLogs = computed(
    () => store.state.refundPreCheckFailureLogs.preCheckFailureLogs
  );
  const refundRequestAssessorNotes = computed(
    () => store.state.refundRequests.refundRequestAssessorNotes
  );
  const refundRequestRequestorNotes = computed(
    () => store.state.refundRequests.refundRequestRequestorNotes
  );

  const showPreCheckLogs = logs => {
    store.commit("refundPreCheckFailureLogs/setLogs", logs);
    cash("#header-footer-slide-over-preview").modal("show");
  };

  const showAssessorNote = (assessor, notes) => {
    store.commit("refundRequests/setRefundRequestAssessorNotes", {
      name: assessor,
      messages: notes
    });
    cash("#assessor-note-slide-over-preview").modal("show");
  };

  const showRequestorNote = (requestor, note) => {
    store.commit("refundRequests/setRefundRequestRequestorNotes", {
      name: requestor,
      message: note
    });
    cash("#requestor-note-slide-over-preview").modal("show");
  };

  function showApprovalModal(uuid) {
    approvalRejectionDataObject.uuid = uuid;
    approvalRejectionDataObject.note = "";
    approvalRejectionDataObject.title = "Approval";
    approvalRejectionDataObject.type = "approval";
    cash("#approval-rejection-modal").modal("show");
  }

  function showRejectionModal(uuid) {
    approvalRejectionDataObject.uuid = uuid;
    approvalRejectionDataObject.note = "";
    approvalRejectionDataObject.title = "Rejection";
    approvalRejectionDataObject.type = "rejection";
    cash("#approval-rejection-modal").modal("show");
  }

  const refundRequestList = computed(
    () => store.state.refundRequests.refundRequestList
  );

  const refundRequestQueryParam = computed(
    () => store.state.refundRequests.refundRequestQueryParam
  );

  const refundRequestListUpdateCounter = computed(
    () => store.state.refundRequests.refundRequestListUpdateCounter
  );

  watch(
    () => refundRequestListUpdateCounter.value,
    () => {
      tabulator.value.replaceData();
    }
  );

  const excelReportBlob = computed(
    () => store.state.refundRequests.excelReportBlob
  );

  watch(
    () => excelReportBlob.value,
    excelData => {
      fileDownload(excelData, "refund-requests.xlsx");
    }
  );

  const refundRequestSuccessMessage = computed(
    () => store.state.refundRequests.refundRequestSuccessMessage
  );

  watch(
    () => refundRequestSuccessMessage.value,
    message => {
      if (message) {
        Swal.fire(buildSwalSuccess(message)).then(confirmed => {
          if (confirmed.isConfirmed) {
            store.commit("refundRequests/clearMessages");
          }
        });
      }
    }
  );

  const refundRequestAlertMessage = computed(
    () => store.state.refundRequests.refundRequestAlertMessage
  );

  watch(
    () => refundRequestAlertMessage.value,
    message => {
      if (message) {
        Swal.fire(buildSwalWarning(message)).then(confirmed => {
          if (confirmed.isConfirmed) {
            store.commit("refundRequests/clearAlertMessages");
          }
        });
      }
    }
  );

  const refundRequestErrorMessage = computed(
    () => store.state.refundRequests.refundRequestErrorMessage
  );

  watch(
    () => refundRequestErrorMessage.value,
    message => {
      if (message) {
        Swal.fire(buildSwalHtmlError(message)).then(confirmed => {
          if (confirmed.isConfirmed) {
            store.commit("refundRequests/clearMessages");
          }
        });
      }
    }
  );

  function onResetStatus(e) {
    Swal.fire(buildSwalWarning("Are you sure to renew?", true)).then(
      confirmed => {
        if (confirmed.isConfirmed) {
          store.commit("main/setFullPageLoading");
          store.dispatch("refundRequests/resetRefundRequestStatus", e);
        }
      }
    );
  }

  function onApprovalSubmit() {
    store.commit("main/setFullPageLoading");
    store.dispatch("refundRequests/sendForRefund", approvalRejectionDataObject);
    cash("#approval-rejection-modal").modal("hide");
  }

  function onRejectionSubmit() {
    store.commit("main/setFullPageLoading");
    store.dispatch(
      "refundRequests/sendForRejection",
      approvalRejectionDataObject
    );
    cash("#approval-rejection-modal").modal("hide");
  }

  function getRefundRequestsAction() {
    store.dispatch("refundRequests/getRefundRequestList");
  }

  onMounted(() => {
    initTabulator();
    reInitOnResizeWindow();
  });

  const initTabulator = () => {
    tabulator.value = new Tabulator(tableRef.value, {
      ajaxURL: process.env.VUE_APP_BASE_URL + "/refundRequests" + baseUrlSuffix,
      ajaxConfig: {
        method: "GET", //set request type to GET
        headers: authGetterService.getAuthHeader()
      },
      ajaxRequesting: function(url, params) {
        store.commit("refundRequests/setRefundRequestQueryParam", {
          params,
          statusParam
        });
        //url - the URL of the request
        //params - the parameters passed with the request
      },
      ajaxResponse: function(url, params, response) {
        //url - the URL of the request
        //params - the parameters passed with the request
        //response - the JSON object returned in the body of the response.
        totalRefundedFund.value =
          response.total_amount === undefined || response.total_amount === null
            ? 0
            : response.total_amount;
        totalRefunds.value =
          response.total_refunds_reqs === undefined ||
          response.total_refunds_reqs === null
            ? 0
            : response.total_refunds_reqs;
        return response; //return the response data to tabulator
      },
      filterMode: "remote",
      sortMode: "remote",
      printAsHtml: true,
      printStyled: true,
      reactiveData: true, //enable reactive data
      pagination: true,
      paginationMode: "remote",
      paginationSize: 10,
      paginationSizeSelector: [10, 20, 30, 40],
      layout: "fitColumns",
      responsiveLayout: "collapse",
      placeholder: "No matching records found",
      columns: [
        {
          formatter: "responsiveCollapse",
          width: 40,
          minWidth: 30,
          hozAlign: "center",
          resizable: false,
          headerSort: false
        },

        // For HTML table
        {
          title: "CUSTOMER",
          minWidth: 150,
          responsive: 0,
          field: "fullname",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap"><a target="_blank" href="https://fundo.com.au/adminpanel/payments/schedules/${
              cell.getData().application_id
            }"><u>${cell.getData().fullname}</u></a></div>
                <div class="text-gray-600 text-xs whitespace-nowrap"><i>${
              cell.getData().email
            }</i></div>
                <div class="text-gray-600 text-xs whitespace-nowrap">${
              cell.getData().mobile
            }</div>
              </div>`;
          }
        },
        {
          title: "TRANSAC ID",
          minWidth: 100,
          field: "transaction_id",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap">${
              cell.getData().transaction_id
            }</div>
              </div>`;
          }
        },
        {
          title: "AMOUNT",
          minWidth: 100,
          field: "amount",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap">${
              cell.getData().refund_amount
            }</div>
              </div>`;
          }
        },
        {
          title: displayedDate,
          minWidth: 100,
          field: "requested_at",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div>
                <div class="font-medium whitespace-nowrap">${
              cell.getData().refunded_at !== undefined
                ? cell.getData().refunded_at
                : cell.getData().requested_at
            }</div>
                </div>`;
          }
        },
        {
          title: "STATUS",
          minWidth: 100,
          field: "status",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div class="flex items-center lg:justify-center ${
              newStatus.includes(cell.getData().status)
                ? "text-theme-9"
                : errorStatus.includes(cell.getData().status)
                  ? "text-theme-6"
                  : successStatus.includes(cell.getData().status)
                    ? "text-theme-9"
                    : approvedStatus.includes(cell.getData().status)
                      ? "text-blue-400"
                      : cancelledStatus.includes(cell.getData().status)
                        ? "text-yellow-600"
                        : "text-yellow-400"
            }">
                <i data-feather="${
              newStatus.includes(cell.getData().status)
                ? "file-plus"
                : successStatus.includes(cell.getData().status)
                  ? "check-square"
                  : approvedStatus.includes(cell.getData().status)
                    ? "user-check"
                    : errorStatus.includes(cell.getData().status)
                      ? "alert-octagon"
                      : cancelledStatus.includes(cell.getData().status)
                        ? "slash"
                        : "loader"
            }" class="w-4 h-4 mr-2"></i> ${cell.getData().status}
              </div>`;
          }
        },
        {
          title: "REQUESTED BY",
          minWidth: 100,
          field: "status",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            return `<div class="flex items-center lg:justify-center text-gray-600">
                <i data-feather="user" class="w-4 h-4 mr-2"></i>${
              cell.getData().requestor
            }
              </div>`;
          }
        },
        {
          title: "ACTIONED BY",
          minWidth: 100,
          field: "actioned_by",
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            if (cell.getData().assessor !== null) {
              return `<div class="flex items-center lg:justify-center text-gray-600">
                <i data-feather="user" class="w-4 h-4 mr-2"></i>${
                cell.getData().assessor
              }
              </div>`;
            } else {
              return "-";
            }
          }
        },
        {
          title: "NOTES",
          minWidth: 100,
          field: "notes",
          responsive: 1,
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            const a = cash(
              `<div class="flex lg:justify-left items-left"></div>`
            );
            const b = cash(
              `<div class="flex lg:justify-left items-left"></div>`
            );
            const c = cash(`<div></div>`);

            if (cell.getData().note !== null) {
              cash(a)
                .append(`<a class="flex items-left text-green-500 mr-3" href="javascript:;">
                  <i data-feather="list" class="w-4 h-4 mr-1"></i> Request Note
                </a>`);
              cash(cash(a).children()[cash(a).children().length - 1]).on(
                "click",
                function() {
                  showRequestorNote(
                    cell.getData().requestor,
                    cell.getData().note
                  );
                }
              );
            }

            if (
              cell.getData().assessor_note !== null &&
              JSON.parse(cell.getData().assessor_note).length > 0
            ) {
              cash(b)
                .append(`<a class="flex items-left text-blue-500 mr-3" href="javascript:;">
                  <i data-feather="list" class="w-4 h-4 mr-1"></i> Manager Note
                </a>`);
              cash(cash(b).children()[cash(b).children().length - 1]).on(
                "click",
                function() {
                  showAssessorNote(
                    cell.getData().assessor,
                    JSON.parse(cell.getData().assessor_note)
                  );
                }
              );
            }

            cash(c).append(a[0]);
            cash(c).append(b[0]);

            return c[0];
          }
        },
        {
          title: "ACTIONS",
          minWidth: 150,
          field: "actions",
          responsive: 1,
          hozAlign: "center",
          vertAlign: "middle",
          print: false,
          download: false,
          formatter(cell) {
            const a = cash(
              `<div class="flex lg:justify-left items-left"></div>`
            );
            const c = cash(`<div></div>`);
            if (
              (errorStatus.includes(cell.getData().status) ||
                cancelledStatus.includes(cell.getData().status)) &&
              adminUser.value.permissions.includes(
                "refundsreq.update.status.renew"
              )
            ) {
              cash(a)
                .append(`<a class="flex items-left text-blue-400 mr-3" href="javascript:;">
                  <i data-feather="refresh-ccw" class="w-4 h-4 mr-1"></i> Renew
                </a>`);
              cash(cash(a).children()[0]).on("click", function() {
                onResetStatus(cell.getData().uuid);
              });
              cash(c).append(a[0]);
            } else if (
              newStatus.includes(cell.getData().status) &&
              adminUser.value.permissions.includes(
                "refundsreq.update.assessed_by"
              )
            ) {
              cash(a)
                .append(`<a class="flex items-left text-theme-9 mr-3" href="javascript:;">
                  <i data-feather="check-circle" class="w-4 h-4 mr-1"></i> Approve
                </a><a class="flex items-left text-red-500 mr-3" href="javascript:;">
                  <i data-feather="x-circle" class="w-4 h-4 mr-1"></i> Reject
                </a>`);
              cash(cash(a).children()[0]).on("click", function() {
                showApprovalModal(cell.getData().uuid);
              });
              cash(cash(a).children()[1]).on("click", function() {
                showRejectionModal(cell.getData().uuid);
              });
              cash(c).append(a[0]);
            }

            if (
              typeof cell.getData().pre_check_failure_logs !== "undefined" &&
              cell.getData().pre_check_failure_logs.length > 0
            ) {
              const b = cash(
                `<div class="flex lg:justify-left items-left"></div>`
              );
              cash(b)
                .append(`<a class="flex items-left text-yellow-500 mr-3" href="javascript:;">
                  <i data-feather="list" class="w-4 h-4 mr-1"></i> Pre Check Logs
                </a>`);
              cash(cash(b).children()[cash(b).children().length - 1]).on(
                "click",
                function() {
                  showPreCheckLogs(cell.getData().pre_check_failure_logs);
                }
              );
              cash(c).append(b[0]);
            }
            return c[0];
          }
        }
        // For print format
      ]
    });

    tabulator.value.on("dataLoadError", function(errorResponse) {
      handleErrorResponse(errorResponse);
    });

    tabulator.value.on("renderComplete", function() {
      feather.replace({
        "stroke-width": 1.5
      });
    });
  };

  // Redraw table onresize
  const reInitOnResizeWindow = () => {
    window.addEventListener("resize", () => {
      tabulator.value.redraw();
      feather.replace({
        "stroke-width": 1.5
      });
    });
  };

  // Filter function
  const onFilter = () => {
    const filterBuilder = [];
    filterBuilder.push({
      field: filter.field,
      type: filter.type,
      value: filter.value
    });
    if (fromDate.value !== "") {
      filterBuilder.push({
        field: fromDate.field,
        type: fromDate.type,
        value: fromDate.value
      });
    }
    if (toDate.value !== "") {
      filterBuilder.push({
        field: toDate.field,
        type: toDate.type,
        value: toDate.value
      });
    }

    tabulator.value.setFilter(filterBuilder);
  };

  // On reset filter
  const onResetFilter = () => {
    filter.field = "fullname";
    filter.type = "like";
    filter.value = "";

    fromDate.field = "from";
    fromDate.type = ">=";
    fromDate.value = "";

    toDate.field = "to";
    toDate.type = "<=";
    toDate.value = "";
    onFilter();
  };

  // Export
  const onExportCsv = () => {
    tabulator.value.download("csv", "data.csv");
  };

  const onExportJson = () => {
    tabulator.value.download("json", "data.json");
  };

  const onExportXlsx = () => {
    const win = window;
    win.XLSX = xlsx;
    tabulator.value.download("xlsx", "data.xlsx", {
      sheetName: "Products"
    });
  };

  const onExportHtml = () => {
    tabulator.value.download("html", "data.html", {
      style: true
    });
  };

  // Print
  const onPrint = () => {
    tabulator.value.print();
  };

  const onExport = () => {
    store.commit("main/setFullPageLoading");
    store.dispatch(
      "refundRequests/requestForExportingList",
      refundRequestQueryParam.value
    );
  };

  return {
    totalRefundedFund,
    totalRefunds,
    adminUser,
    refundPreCheckFailureLogs,
    refundRequestAssessorNotes,
    refundRequestRequestorNotes,
    showPreCheckLogs,
    tableRef,
    fromDate,
    toDate,
    filter,
    onFilter,
    onResetFilter,
    onExportCsv,
    onExportJson,
    onExportXlsx,
    onExportHtml,
    onPrint,
    onExport,
    path,
    refundRequestList,
    getRefundRequestsAction,
    approvalRejectionDataObject,
    onApprovalSubmit,
    onRejectionSubmit
  };
};
